import { onUnmounted, ref } from 'vue';

import * as DataDogLogs from '~/lib/datadog/datadog';

export function useVideo(videoId) {
  const videoRef = ref(null);
  const isPlaying = ref(false);
  let lazyVideoObserver = null;

  const lazyLoadVideo = function () {
    const lazyVideo = document.getElementById(videoId);

    if (!lazyVideo) {
      DataDogLogs.warn(
        `[Allbirds composable] - useVideo - lazyLoadVideo - Video element not found`,
        {
          videoId,
        },
      );
      return;
    }

    lazyVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const videoElement = entry.target;
            const sources = videoElement.querySelectorAll('source[data-src]');

            sources.forEach((source) => {
              const videoUrl = source.dataset.src;
              source.src = videoUrl;
            });

            videoElement.preload = 'auto';
            videoElement.load();

            // If video has autoplay, try to play it after it's loaded
            if (videoElement.autoplay) {
              // Ensure video is muted for autoplay to work
              videoElement.muted = true;

              // Wait for video to be ready before attempting to play
              const playVideo = () => {
                videoElement
                  .play()
                  .then(() => {
                    isPlaying.value = true;
                  })
                  .catch((error) => {
                    DataDogLogs.error(
                      `[Allbirds composable] - useVideo - lazyLoadVideo - Failed to autoplay video`,
                      {
                        videoId,
                      },
                      error,
                    );
                    isPlaying.value = false;
                  });
              };

              if (videoElement.readyState >= 2) {
                // Video is ready, play immediately
                playVideo();
              } else {
                videoElement.addEventListener('canplay', playVideo, { once: true });
                // Also listen for canplaythrough as fallback
                videoElement.addEventListener('canplaythrough', playVideo, { once: true });
              }
            } else {
              DataDogLogs.warn(
                `[Allbirds composable] - useVideo - lazyLoadVideo - Video does not have autoplay attribute`,
                {
                  videoId,
                },
              );
            }

            lazyVideoObserver.unobserve(videoElement);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before the video comes into view
        threshold: 0.1, // Trigger when 10% of the video is visible
      },
    );

    lazyVideoObserver.observe(lazyVideo);
  };

  const togglePlayPause = function (play) {
    if (!videoRef.value) return;
    isPlaying.value = play ?? !isPlaying.value;

    if (isPlaying.value) {
      videoRef.value.play().catch((error) => {
        DataDogLogs.error(
          `[Allbirds composable] - useVideo - togglePlayPause - Failed to play video`,
          {
            videoId,
          },
          error,
        );
        isPlaying.value = false;
      });
    } else {
      videoRef.value.pause();
    }
  };

  function init() {
    videoRef.value = document.getElementById(videoId);
    lazyLoadVideo();
  }

  onUnmounted(() => {
    if (lazyVideoObserver) {
      lazyVideoObserver.disconnect();
    }
  });

  return {
    init,
    isPlaying,
    togglePlayPause,
  };
}
